import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";

/**
 * LoggingService provides centralized logging functionality for the application.
 *
 * IMPORTANT: This service is designed to avoid circular dependencies with HTTP_INTERCEPTORS.
 * The dependency chain that creates the circular reference is:
 * HTTP_INTERCEPTORS → AuthenticationInterceptorService → SessionService → LoggingService → ConfigService → HttpClient → HTTP_INTERCEPTORS
 *
 * To break this chain:
 * 1. ConfigService is injected as @Optional()
 * 2. We don't call configService.loadConfig() during initialization
 * 3. We provide an updateFromConfig() method that should be called after app initialization
 */
@Injectable({
    providedIn: "root",
})
export class LoggingService {
    constructor() {
        this.initializeService();
    }

    private static defaultEnableLogs = true;
    private enableLogs = true;

    /**
     * Static method for logging when dependency injection is not available
     * @param message The message to log
     * @param type The type of log ('log', 'warn', 'error', 'debug', 'table')
     * @param always Whether to display the log regardless of logging settings
     * @param optionalParams Additional parameters to log
     */
    public static staticLog(
        message: any,
        type: "log" | "warn" | "error" | "debug" | "table" = "log",
        always: boolean = false,
        ...optionalParams: any[]
    ): void {
        if (always || LoggingService.defaultEnableLogs) {
            switch (type) {
                case "log":
                    console.log(message, ...optionalParams);
                    break;
                case "warn":
                    console.warn(message, ...optionalParams);
                    break;
                case "error":
                    console.error(message, ...optionalParams);
                    break;
                case "debug":
                    // tslint:disable-next-line:no-console
                    console.debug(message, ...optionalParams);
                    break;
                case "table":
                    console.table(message, optionalParams.length > 0 ? optionalParams[0] : undefined);
                    break;
            }
        }
    }

    private async initializeService(): Promise<void> {
        // Get the enableConsoleLogs flag from environment
        try {
            this.enableLogs = environment.logging?.enable ?? false;
        } catch (error) {
            // If there's an error getting the config, default to false
            this.enableLogs = false;
            this.warn("Failed to get enableConsoleLogs config, defaulting to false", false);
        }
    }

    /**
     * Log an informational message to the console
     * @param message The message to log
     * @param always Whether to display the log regardless of logging settings
     * @param optionalParams Additional parameters to log
     */
    public log(message: any, always: boolean = false, ...optionalParams: any[]): void {
        if (always || this.enableLogs) {
            console.log(message, ...optionalParams);
        }
    }

    /**
     * Log a warning message to the console
     * @param message The message to log
     * @param always Whether to display the log regardless of logging settings
     * @param optionalParams Additional parameters to log
     */
    public warn(message: any, always: boolean = false, ...optionalParams: any[]): void {
        if (always || this.enableLogs) {
            console.warn(message, ...optionalParams);
        }
    }

    /**
     * Log an error message to the console
     * @param message The message to log
     * @param always Whether to display the log regardless of logging settings
     * @param optionalParams Additional parameters to log
     */
    public error(message: any, always: boolean = false, ...optionalParams: any[]): void {
        if (always || this.enableLogs) {
            console.error(message, ...optionalParams);
        }
    }

    /**
     * Log debug information to the console
     * @param message The message to log
     * @param always Whether to display the log regardless of logging settings
     * @param optionalParams Additional parameters to log
     */
    public debug(message: any, always: boolean = false, ...optionalParams: any[]): void {
        if (always || this.enableLogs) {
            // tslint:disable-next-line:no-console
            console.debug(message, ...optionalParams);
        }
    }

    /**
     * Log information as a table
     * @param tabularData The data to display in a table
     * @param always Whether to display the table regardless of logging settings
     * @param properties Optional array of property names to include in the table
     */
    public table(tabularData: any, always: boolean = false, properties?: string[]): void {
        if (always || this.enableLogs) {
            console.table(tabularData, properties);
        }
    }

    /**
     * Check if logging is currently enabled
     * @returns boolean indicating if logging is enabled
     */
    public isLoggingEnabled(): boolean {
        return this.enableLogs;
    }

    /**
     * Enable or disable logging programmatically
     * Note: This will be overridden if the app is reloaded
     * @param enable Whether to enable logging
     */
    public setLoggingEnabled(enable: boolean): void {
        this.enableLogs = enable;
    }

    /**
     * Update logging settings from config
     * This should be called after the app is fully initialized
     */
    public updateFromConfig(): void {
        try {
            this.enableLogs = environment.logging?.enable ?? false;
        } catch (error) {
            this.warn("Failed to update logging settings from environment", false);
        }
    }

    // Backward compatibility methods

    /**
     * @deprecated Use log(message, true, ...optionalParams) instead
     */
    public logAlways(message: any, ...optionalParams: any[]): void {
        this.log(message, true, ...optionalParams);
    }

    /**
     * @deprecated Use warn() instead
     */
    public logWarning(message: any, ...optionalParams: any[]): void {
        this.warn(message, false, ...optionalParams);
    }

    /**
     * @deprecated Use error() instead
     */
    public logError(message: any, ...optionalParams: any[]): void {
        this.error(message, false, ...optionalParams);
    }

    /**
     * @deprecated Use debug() instead
     */
    public logDebug(message: any, ...optionalParams: any[]): void {
        this.debug(message, false, ...optionalParams);
    }

    /**
     * @deprecated Use table() instead
     */
    public logTable(tabularData: any, properties?: string[]): void {
        this.table(tabularData, false, properties);
    }
}
